import React, { useState, useEffect } from "react";
import {
  Code,
  Palette,
  Briefcase,
  MessageCircle,
  BookOpen,
  Target,
  Sparkles,
  ExternalLink,
  Video,
  FileText,
  GraduationCap,
  Book as BookIcon,
} from "lucide-react";
import { apiService } from "../services/api";
import styles from "./LearningPaths.module.css";

// Skill to learning path mapping (moved outside component for stability)
const skillToPathMapping = {
  // Programming & Development
  javascript: ["full-stack-developer", "frontend-developer", "web-developer"],
  nodejs: ["full-stack-developer", "backend-developer", "api-developer"],
  react: ["full-stack-developer", "frontend-developer", "react-specialist"],
  python: ["data-scientist", "backend-developer", "ml-engineer"],
  java: ["backend-developer", "enterprise-developer", "android-developer"],
  "c++": ["systems-programmer", "game-developer", "embedded-systems"],
  sql: ["data-scientist", "database-administrator", "backend-developer"],
  mongodb: ["full-stack-developer", "backend-developer", "database-specialist"],

  // Design & Creative
  "ui/ux": ["ui-ux-designer", "product-designer", "interaction-designer"],
  figma: ["ui-ux-designer", "product-designer", "visual-designer"],
  design: ["ui-ux-designer", "graphic-designer", "product-designer"],
  photoshop: ["graphic-designer", "visual-designer", "digital-artist"],

  // Business & Management
  "project management": ["project-manager", "scrum-master", "product-manager"],
  agile: ["project-manager", "scrum-master", "agile-coach"],
  scrum: ["project-manager", "scrum-master", "agile-practitioner"],
  marketing: ["digital-marketing", "content-strategist", "growth-marketer"],

  // Soft Skills
  leadership: ["leadership-communication", "team-lead", "manager"],
  communication: ["leadership-communication", "business-analyst", "consultant"],
};

const LearningPaths = () => {
  const [learningPaths, setLearningPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userSkills, setUserSkills] = useState([]);
  const [recommendedPaths, setRecommendedPaths] = useState([]);
  const [mlLearningPath, setMlLearningPath] = useState(null);
  const [showMLPath, setShowMLPath] = useState(false);

  // Category and icon mapping for dynamic paths
  const getCategoryAndIcon = (skillName) => {
    const skill = skillName?.toLowerCase() || "";

    if (
      skill.includes("design") ||
      skill.includes("ui") ||
      skill.includes("ux") ||
      skill.includes("figma")
    ) {
      return { category: "Design & Creative", icon: Palette, color: "#8b5cf6" };
    }
    if (
      skill.includes("management") ||
      skill.includes("agile") ||
      skill.includes("scrum") ||
      skill.includes("marketing")
    ) {
      return {
        category: "Business & Management",
        icon: Briefcase,
        color: "#f59e0b",
      };
    }
    if (skill.includes("leadership") || skill.includes("communication")) {
      return { category: "Soft Skills", icon: MessageCircle, color: "#10b981" };
    }
    // Default to Technical Skills
    return { category: "Technical Skills", icon: Code, color: "#3b82f6" };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user's assessment history to identify their skills
        const dashboardData = await apiService.progress.getDashboard();

        console.log("Dashboard data:", dashboardData);

        if (dashboardData?.recentAssessments) {
          // Extract unique skills from user's assessments
          const skills = [
            ...new Set(
              dashboardData.recentAssessments
                .map((attempt) => attempt.skillName)
                .filter(Boolean),
            ),
          ];
          setUserSkills(skills);
          console.log("User skills from assessments:", skills);
          console.log("Number of unique skills found:", skills.length);
        } else {
          console.log("No recent assessments found in dashboard data");
        }

        // Fetch learning paths from API
        const paths = await apiService.courses.getLearningPaths();
        console.log("Learning paths from API:", paths);
        const transformedPaths = paths.map((path) => ({
          id: path.id,
          title: path.title,
          description: path.description,
          category: "Technical Skills",
          level: path.difficultyLevel,
          duration: `${path.estimatedDuration || 8} weeks`,
          modules: path.courseCount,
          icon: Code,
          color: "#3b82f6",
          rating: 4.7,
          enrollments: 1000,
          progress: 0,
          isEnrolled: false,
          skills: ["JavaScript", "React"],
          creatorName: path.creatorName,
          createdAt: path.createdAt,
        }));
        setLearningPaths(transformedPaths);

        // Fetch ML-powered learning path
        try {
          const mlPathData =
            await apiService.mlRecommendations.getLearningPath(20);
          console.log("ML Learning Path:", mlPathData);
          if (mlPathData?.success && mlPathData?.data?.learningPath) {
            setMlLearningPath(mlPathData.data);
            setShowMLPath(true);
          }
        } catch (error) {
          console.log("ML recommendations not available yet:", error);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Generate dynamic learning paths based on user's assessment skills
  useEffect(() => {
    if (userSkills.length > 0) {
      console.log("Generating recommendations for skills:", userSkills);
      const dynamicPaths = [];
      const addedPathIds = new Set();

      userSkills.forEach((skillName) => {
        const skillKey = skillName.toLowerCase().trim();
        const { category, icon, color } = getCategoryAndIcon(skillName);

        // Try exact match first
        let matchingPathIds = skillToPathMapping[skillKey] || [];

        // If no exact match, try fuzzy matching
        if (matchingPathIds.length === 0) {
          // Check if skill contains any mapping key
          for (const [key, paths] of Object.entries(skillToPathMapping)) {
            if (skillKey.includes(key) || key.includes(skillKey)) {
              matchingPathIds = paths;
              console.log(`Fuzzy match: "${skillName}" matched with "${key}"`);
              break;
            }
          }
        }

        console.log(
          `Skill "${skillName}" (${skillKey}) -> Paths:`,
          matchingPathIds,
        );

        if (matchingPathIds.length === 0) {
          console.warn(`No learning paths found for skill: "${skillName}"`);
          // Create a generic path for unmapped skills
          const genericPathId = `${skillKey.replace(/[^a-z0-9]/g, "-")}-specialist`;
          matchingPathIds = [genericPathId];
        }

        matchingPathIds.forEach((pathId) => {
          if (!addedPathIds.has(pathId)) {
            addedPathIds.add(pathId);

            // Create dynamic path based on skill
            const pathTitle = pathId
              .split("-")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ");

            dynamicPaths.push({
              id: pathId,
              title: pathTitle,
              description: `Master ${skillName} and related technologies to advance your career`,
              category,
              level: "Intermediate",
              duration: "10 weeks",
              modules: 7,
              icon,
              color,
              rating: 4.7,
              enrollments: Math.floor(Math.random() * 2000) + 500,
              progress: 0,
              isEnrolled: false,
              skills: [skillName, "Best Practices", "Advanced Concepts"],
              isRecommended: true,
            });
          }
        });
      });

      console.log("Generated recommended paths:", dynamicPaths);
      setRecommendedPaths(dynamicPaths);
    } else {
      console.log("No user skills found, no recommendations generated");
      setRecommendedPaths([]);
    }
  }, [userSkills]);

  if (loading) {
    return (
      <div className={styles.learningPathsPage}>
        <div className="loading-spinner">Loading learning paths...</div>
      </div>
    );
  }

  // Static fallback paths for users without assessment history
  // Currently not displayed - kept for future reference
  // const mockPaths = [...];

  // Combine paths intelligently:
  // 1. Show recommended paths first (if user has assessments)
  // 2. Show API learning paths
  const allPaths = [...recommendedPaths, ...learningPaths];

  console.log("All paths count:", {
    recommended: recommendedPaths.length,
    fromAPI: learningPaths.length,
    total: allPaths.length,
  });

  const getLevelColor = (level) => {
    switch (level) {
      case "Beginner":
      case "beginner":
        return "#10b981";
      case "Intermediate":
      case "intermediate":
        return "#f59e0b";
      case "Advanced":
      case "advanced":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case "video":
        return Video;
      case "article":
        return FileText;
      case "course":
        return GraduationCap;
      case "book":
        return BookIcon;
      default:
        return BookOpen;
    }
  };

  const getResourceTypeColor = (type) => {
    switch (type) {
      case "video":
        return "#ef4444";
      case "article":
        return "#3b82f6";
      case "course":
        return "#8b5cf6";
      case "book":
        return "#10b981";
      default:
        return "#6b7280";
    }
  };

  return (
    <div className={styles.learningPathsPage}>
      <div className={styles.learningPathsContainer}>
        {/* <div className={styles.learningPathsHeader}>
          <div className={styles.headerContent}>
            <h1>Learning Paths</h1>
            <p>
              Structured learning journeys to master new skills and advance your
              career
            </p>
          </div>

          <div className={styles.learningStats}>
            <div className={styles.statItem}>
              <BookOpen size={20} />
              <span>{enrolledPaths.length} Active Paths</span>
            </div>
            <div className={styles.statItem}>
              <Target size={20} />
              <span>
                {completedModules}/{totalModules} Modules
              </span>
            </div>
          </div>
        </div> */}

        {/* <div className={styles.learningPathsFilters}>
          <div className={styles.searchBar}>
            <Search size={20} />
            <input
              type="text"
              placeholder="Search learning paths..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className={styles.filterControls}>
            <div className={styles.filterGroup}>
              <label>Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label>Level</label>
              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
              >
                {levels.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div> */}

        {recommendedPaths.length > 0 && (
          <div className={styles.recommendedSection}>
            <div className={styles.recommendedHeader}>
              <Sparkles size={24} />
              <div>
                <h2>Recommended for You</h2>
                <p>
                  Based on your assessment history in: {userSkills.join(", ")}
                </p>
              </div>
            </div>
          </div>
        )}

        {showMLPath &&
          mlLearningPath &&
          mlLearningPath.learningPath.length > 0 && (
            <div className={styles.mlLearningPathSection}>
              <div className={styles.mlPathHeader}>
                <div className={styles.headerIcon}>
                  <Sparkles size={28} />
                </div>
                <div className={styles.headerContent}>
                  <h2>AI-Powered Learning Path</h2>
                  <p>
                    Personalized recommendations based on your assessment
                    performance and skill gaps
                  </p>
                </div>
              </div>

              {mlLearningPath.summary && (
                <div className={styles.mlPathSummary}>
                  <div className={styles.summaryStats}>
                    <div className={styles.stat}>
                      <span className={styles.statLabel}>Average Proficiency</span>
                      <span className={styles.statValue}>
                        {mlLearningPath.summary.average_proficiency}%
                      </span>
                    </div>
                    <div className={styles.stat}>
                      <span className={styles.statLabel}>Skills to Improve</span>
                      <span className={styles.statValue}>
                        {mlLearningPath.summary.skills_needing_work}
                      </span>
                    </div>
                    <div className={styles.stat}>
                      <span className={styles.statLabel}>Total Resources</span>
                      <span className={styles.statValue}>
                        {mlLearningPath.summary.total_resources}
                      </span>
                    </div>
                    <div className={styles.stat}>
                      <span className={styles.statLabel}>Est. Time</span>
                      <span className={styles.statValue}>
                        {mlLearningPath.summary.estimated_time_hours}h
                      </span>
                    </div>
                  </div>
                  <div className={styles.summaryRecommendation}>
                    <strong>Recommendation:</strong>{" "}
                    {mlLearningPath.summary.recommendation}
                  </div>
                </div>
              )}

              <div className={styles.mlLearningPathGrid}>
                {mlLearningPath.learningPath.map((skillPath, index) => (
                  <div key={skillPath.skill_id} className={styles.mlSkillPathCard}>
                    <div className={styles.skillPathHeader}>
                      <div className={styles.skillPriority}>
                        <span className={styles.priorityBadge}>
                          Priority #{index + 1}
                        </span>
                        <span className={styles.priorityScore}>
                          Score: {skillPath.priority_score}
                        </span>
                      </div>
                      <h3>{skillPath.skill_name}</h3>
                      <p className={styles.skillCategory}>{skillPath.category}</p>
                    </div>

                    <div className={styles.skillPathStats}>
                      <div className={styles.statItem}>
                        <span className={styles.label}>Current Proficiency</span>
                        <div className={styles.proficiencyBar}>
                          <div
                            className={styles.proficiencyFill}
                            style={{
                              width: `${skillPath.proficiency_score}%`,
                              backgroundColor:
                                skillPath.proficiency_score < 40
                                  ? "#ef4444"
                                  : skillPath.proficiency_score < 70
                                    ? "#f59e0b"
                                    : "#10b981",
                            }}
                          ></div>
                          <span className={styles.proficiencyText}>
                            {skillPath.proficiency_score}%
                          </span>
                        </div>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.label}>Recommended Level</span>
                        <span
                          className={styles.levelBadge}
                          style={{
                            backgroundColor: getLevelColor(
                              skillPath.recommended_difficulty,
                            ),
                          }}
                        >
                          {skillPath.recommended_difficulty}
                        </span>
                      </div>
                      {skillPath.improvement_trend !== 0 && (
                        <div className={styles.statItem}>
                          <span className={styles.label}>Trend</span>
                          <span
                            className={`trend ${skillPath.improvement_trend > 0 ? "positive" : "negative"}`}
                          >
                            {skillPath.improvement_trend > 0 ? "↑" : "↓"}{" "}
                            {Math.abs(skillPath.improvement_trend).toFixed(1)}%
                          </span>
                        </div>
                      )}
                    </div>

                    <div className={styles.learningObjective}>
                      <Target size={16} />
                      <span>{skillPath.learning_objective}</span>
                    </div>

                    <div className={styles.skillResources}>
                      <h4>
                        Recommended Resources ({skillPath.resources.length})
                      </h4>
                      <div className={styles.resourcesList}>
                        {skillPath.resources.map((resource) => {
                          const ResourceIcon = getResourceIcon(resource.type);
                          return (
                            <div
                              key={resource.resource_id}
                              className={styles.resourceItem}
                            >
                              <div
                                className={styles.resourceIconSmall}
                                style={{
                                  color: getResourceTypeColor(resource.type),
                                }}
                              >
                                <ResourceIcon size={16} />
                              </div>
                              <div className={styles.resourceInfo}>
                                <span className={styles.resourceTitle}>
                                  {resource.title}
                                </span>
                                <span className={styles.resourceMeta}>
                                  {resource.type} •{" "}
                                  {resource.estimated_time_minutes} min •{" "}
                                  {resource.difficulty_level}
                                </span>
                                <span className={styles.resourceReason}>
                                  {resource.reason}
                                </span>
                              </div>
                              <a
                                href={resource.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.resourceLinkBtn}
                              >
                                <ExternalLink size={14} />
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Learning Resources Section */}
        {/* <div className={styles.learningResourcesSection}>
          <div className={styles.resourcesHeader}>
            <div className={styles.headerContent}>
              <h2>Learning Resources</h2>
              <p>Curated resources to help you master new skills</p>
            </div>
            <button
              className={styles.toggleResourcesBtn}
              onClick={() => setShowResources(!showResources)}
            >
              {showResources ? "Hide Resources" : "Show Resources"}
            </button>
          </div>

          {showResources && (
            <>
              {learningResources.length > 0 ? (
                <div className={styles.resourcesGrid}>
                  {learningResources
                    .filter((resource) => {
                      const matchesSearch =
                        resource.title
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) ||
                        resource.skills?.some((skill) =>
                          skill.skill_name
                            ?.toLowerCase()
                            .includes(searchTerm.toLowerCase()),
                        );
                      const matchesLevel =
                        selectedLevel === "All" ||
                        resource.difficultyLevel?.toLowerCase() ===
                          selectedLevel.toLowerCase();
                      return matchesSearch && matchesLevel;
                    })
                    .slice(0, 12)
                    .map((resource) => {
                      const ResourceIcon = getResourceIcon(resource.type);
                      const typeColor = getResourceTypeColor(resource.type);
                      return (
                        <div key={resource.id} className={styles.resourceCard}>
                          <div className={styles.resourceHeader}>
                            <div
                              className={styles.resourceIcon}
                              style={{
                                backgroundColor: `${typeColor}20`,
                                color: typeColor,
                              }}
                            >
                              <ResourceIcon size={20} />
                            </div>
                            <span
                              className={styles.resourceType}
                              style={{ color: typeColor }}
                            >
                              {resource.type}
                            </span>
                          </div>

                          <div className={styles.resourceContent}>
                            <h4>{resource.title}</h4>

                            <div className={styles.resourceMeta}>
                              <div className={styles.metaItem}>
                                <Clock size={14} />
                                <span>{resource.estimatedTimeMinutes} min</span>
                              </div>
                              <div
                                className={`${styles.levelBadge} ${styles.small}`}
                                style={{
                                  backgroundColor: getLevelColor(
                                    resource.difficultyLevel,
                                  ),
                                }}
                              >
                                {resource.difficultyLevel}
                              </div>
                            </div>

                            {resource.skills && resource.skills.length > 0 && (
                              <div className={styles.resourceSkills}>
                                {resource.skills.slice(0, 2).map((skill) => (
                                  <span
                                    key={skill.skill_id}
                                    className={`${styles.skillTag} ${styles.small}`}
                                  >
                                    {skill.skill_name}
                                  </span>
                                ))}
                                {resource.skills.length > 2 && (
                                  <span className={`${styles.skillMore} ${styles.small}`}>
                                    +{resource.skills.length - 2}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          <div className={styles.resourceFooter}>
                            <a
                              href={resource.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.resourceLink}
                            >
                              View Resource
                              <ExternalLink size={14} />
                            </a>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className={styles.noResources}>
                  <BookOpen size={48} />
                  <p>No learning resources available yet</p>
                </div>
              )}
            </>
          )}
        </div> */}
      </div>
    </div>
  );
};

export default LearningPaths;
